import {
  DataGridPro,
  GridColDef,
  GridFilterItem,
  GridRenderCellParams,
  GridToolbarContainer,
  GridFilterOperator,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
  GridRowParams,
  GridInitialState,
} from "@mui/x-data-grid-pro";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Box,
  Autocomplete,
  Chip,
  TextField,
  Grid,
  Tab,
  Collapse,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import SvgRender from "../../components/Ranker/svgImage";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import GridViewToolBar from "../../components/common/GridViewToolBar";
import ScreenieGridView from "../../components/Properties/ScreenieGridView";
import {
  allModuleEvents,
  allModulesCategories,
} from "../../components/Analytics/events";
import { useAppDispatch } from "../../app/hooks";
import useEventTracker from "../../components/Analytics/eventHook";

const SubstituteRVisualize = () => {
  const MODEL_NAME = allModulesCategories.substituteR;
  const { state } = useLocation();
  const rowData = state.map((row: any, id: number) => {
    return {
      id: id + 1,
      ...row,
    };
  });
  const [tabValue, setTabValue] = useState<string>("1");
  const [showGridView, setShowGridView] = useState<boolean>(false);
  const [moleculeFilter, setMoleculeFilter] = useState<any>(null);
  const [ro8TableVisible, setRo8TableVisible] = useState<boolean>(false);
  const [toolModel, setToolModel] = useState<{
    [key: string]: GridInitialState;
  }>();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const scrollToRef = useRef(null);
  const table = useGridApiRef();
  const { trackEvent } = useEventTracker(MODEL_NAME);

  useEffect(() => {
    setRo8TableVisible(false);
    const savedTableState = localStorage.getItem("substituteRTableState");
    if (savedTableState) {
      const parsedState = JSON.parse(savedTableState);
      setToolModel(parsedState);
    }
    setRo8TableVisible(true);
  }, []);

  const CustomToolbar = (props: any) => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport
          printOptions={{
            disableToolbarButton: true,
          }}
        />
      </GridToolbarContainer>
    );
  };

  const handleEnantiomersData = (
    params: GridRenderCellParams<any, string[]>
  ) => {
    return (
      <Box display={"flex"} flexWrap={"wrap"}>
        {params.value.map((enantiomer) => {
          return (
            <Chip
              key={enantiomer}
              label={enantiomer}
              sx={{
                m: 0.5,
                color: "none",
              }}
            />
          );
        })}
      </Box>
    );
  };

  const customArrayFilterOperator: GridFilterOperator = {
    label: "contains",
    value: "contains",
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (
        !filterItem.value ||
        !Array.isArray(filterItem.value) ||
        filterItem.value.length === 0
      ) {
        return null;
      }

      return ({ value }) => {
        if (!value || !Array.isArray(value)) {
          return false;
        }
        return filterItem.value.every((selected: any) =>
          value.includes(selected)
        );
      };
    },
    InputComponent: ({ item, applyValue }) => {
      const [selectedValues, setSelectedValues] = useState(item.value || []);
      const handleFilterChange = (event: any, newValue: any) => {
        setSelectedValues(newValue);
        applyValue({ ...item, value: newValue });
      };

      return (
        <Box width={300}>
          <Autocomplete
            sx={{ mt: 2 }}
            multiple
            options={[]}
            value={selectedValues}
            freeSolo
            onChange={handleFilterChange}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="Choose Interactions"
              />
            )}
          />
        </Box>
      );
    },
  };

  const handleScroll = () => {
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    if (!params) return;

    const substituteRTableState = table.current.exportState();
    localStorage.setItem(
      "substituteRTableState",
      JSON.stringify(substituteRTableState)
    );

    const filteredSortedIds = gridFilteredSortedRowIdsSelector(table);
    const filterId = filteredSortedIds.findIndex((idx) => idx === params.id);

    trackEvent(allModuleEvents.evaluation);
    navigate("/leader/r-groups/evaluations", {
      state: {
        id: filterId,
        rowData: rowData,
        evaluationIds: filteredSortedIds,
        data: params.row,
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      width: 150,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
    {
      field: "SMILES",
      headerName: "SMILES",
      width: 350,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "svg",
      headerName: "2D Representation",
      description: "This column has images and is not sortable.",
      sortable: false,
      filterable: false,
      disableExport: true,
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <SvgRender smiles={params.row.SMILES} />,
    },
    {
      field: "has_pains",
      headerName: "Pains",
      width: 120,
      type: "boolean",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "has_unwanted_substructure",
      headerName: "Unwanted Substructure",
      width: 120,
      type: "boolean",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "fails_ro8",
      headerName: "RO8",
      width: 120,
      type: "boolean",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "has_non_good_ring",
      headerName: "Non Good Ring",
      width: 120,
      type: "boolean",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "enantiomers",
      headerName: "Enantiomers",
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: handleEnantiomersData,
      filterOperators: [customArrayFilterOperator],
      width: 500,
    },
  ];

  return (
    <>
      <GridViewToolBar
        tabValue={tabValue}
        gridViewTabs={["1"]}
        handleScroll={handleScroll}
        showGridView={showGridView}
        setShowGridView={setShowGridView}
      />
      <Grid container spacing={2} p={2}>
        <Grid item container px={1} pt={3} height={"100%"}>
          <Grid item width={"100%"} sx={{ overflowY: "auto" }} height={"100%"}>
            <Card sx={{ position: "relative" }}>
              <TabContext value={tabValue}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList
                    value={tabValue}
                    onChange={(event: React.SyntheticEvent, newValue: string) =>
                      setTabValue(newValue)
                    }
                    aria-label="filter tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Predictions" value="1" />
                  </TabList>
                </Box>
                <TabPanel value="1">
                  {ro8TableVisible && (
                    <Box sx={{ height: "50em" }}>
                      <DataGridPro
                        apiRef={table}
                        sx={{
                          "& .MuiDataGrid-columnHeaderTitle": {
                            whiteSpace: "normal",
                            lineHeight: "normal",
                            fontWeight: "bold",
                          },
                          "& .MuiDataGrid-columnHeader": {
                            // Forced to use important since overriding inline styles
                            height: "unset !important",
                          },
                          "& .MuiDataGrid-columnHeaders": {
                            // Forced to use important since overriding inline styles
                            maxHeight: "175px !important",
                            textAlign: "center",
                          },
                        }}
                        rows={rowData}
                        columns={columns}
                        getRowHeight={() => "auto"}
                        pagination
                        initialState={
                          Object.keys(toolModel || {}).length > 0
                            ? toolModel
                            :
                          {
                          pagination: {
                            paginationModel: {
                              pageSize: 10,
                            },
                          },
                        }}
                        onStateChange={(newState) => {
                          setMoleculeFilter(newState.filter.filterModel);
                        }}
                        pageSizeOptions={[10, 20, 30]}
                        onRowClick={(params) => handleRowClick(params)}
                        slots={{
                          toolbar: (props) => (
                            <CustomToolbar props={props} isExport={false} />
                          ),
                        }}
                        slotProps={{
                          filterPanel: {
                            sx: {
                              width: 650,
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                </TabPanel>
              </TabContext>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Collapse in={showGridView}>
        <Box ref={scrollToRef} pb={2}>
          <ScreenieGridView
            isTopLevelEnable={false}
            tabValue="1"
            allRows={rowData}
            filters={[moleculeFilter]}
            model={allModulesCategories.substituteR}
          />
        </Box>
      </Collapse>
    </>
  );
};

export default SubstituteRVisualize;
